import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteModule, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CashierService, ISaveCashierLog } from '../../services/cashier.service';
import { DropdownService } from '../../services/dropdown.service';
import { AppMessageService } from '../../services/app-message.service';
import { CommonService } from '../../services/common.service';
import { CashInOutDialogService, CashierLogTypeCode } from '../../services/cash-in-out-dialog.service';
import { DropdownDataItem } from '../../models/dropdown-data-soure';
import { VMS_DATEPICKER_FORMAT } from '../../common/date-utils';

function normalizeDd(row: any): DropdownDataItem {
    return {
        name: row.name ?? row.Name ?? '',
        value: row.value ?? row.Value ?? row.name ?? row.Name,
        keyID: row.keyID ?? row.KeyID
    };
}

function logDateToIso(d: Date): string {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0).toISOString();
}

function isNonEmptyString(value: string | undefined | null): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

@Component({
    selector: 'app-cash-in-out-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, AutoCompleteModule, DatePickerModule, DialogModule, InputNumberModule, SelectModule],
    styles: [
        `
            :host ::ng-deep .cashier-cash-dialog .p-dialog-header {
                padding: 0.85rem 1.25rem;
                border-bottom: 1px solid var(--surface-border);
                background: linear-gradient(180deg, var(--surface-50) 0%, var(--surface-0) 100%);
            }
            :host ::ng-deep .cashier-cash-dialog .p-dialog-title {
                font-size: 1.1rem;
                font-weight: 700;
            }
            :host ::ng-deep .cashier-cash-dialog .p-dialog-content {
                padding: 1rem 1.25rem 0.75rem;
            }
            :host ::ng-deep .cashier-cash-dialog .p-dialog-footer {
                padding: 0.75rem 1.25rem 1rem;
                border-top: 1px solid var(--surface-border);
                background: var(--surface-50);
            }
            :host ::ng-deep .cashier-cash-dialog .p-select,
            :host ::ng-deep .cashier-cash-dialog .p-autocomplete,
            :host ::ng-deep .cashier-cash-dialog .p-inputnumber,
            :host ::ng-deep .cashier-cash-dialog .p-datepicker {
                width: 100%;
            }
            .cashier-dlg-badge {
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
                padding: 0.2rem 0.55rem;
                border-radius: 999px;
                font-size: 0.7rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.04em;
            }
            .cashier-dlg-badge--in {
                background: color-mix(in srgb, var(--green-500) 14%, transparent);
                color: var(--green-700);
            }
            .cashier-dlg-badge--out {
                background: color-mix(in srgb, var(--red-500) 14%, transparent);
                color: var(--red-700);
            }
            .cashier-dlg-form {
                width: 100%;
                max-width: 36rem;
                margin: 0 auto;
                border-radius: 12px;
                border: 1px solid var(--surface-border);
                background: var(--surface-0);
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
                padding: 0.85rem 1rem;
            }
            .cashier-dlg-2col {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 0.55rem 0.85rem;
                align-items: start;
            }
            .cashier-dlg-span2 {
                grid-column: 1 / -1;
            }
            @media (max-width: 479.98px) {
                .cashier-dlg-2col {
                    grid-template-columns: 1fr;
                }
                .cashier-dlg-span2 {
                    grid-column: auto;
                }
            }
        `
    ],
    template: `
        <p-dialog
            [(visible)]="visible"
            [modal]="true"
            [draggable]="false"
            [resizable]="false"
            [closable]="false"
            [dismissableMask]="false"
            [style]="{ width: 'min(38rem, 96vw)' }"
            styleClass="custom-dialog cashier-cash-dialog"
            (onHide)="onHide()">
            <ng-template pTemplate="header">
                <div class="flex align-items-center gap-2">
                    <span
                        class="cashier-dlg-badge"
                        [ngClass]="logType === 1 ? 'cashier-dlg-badge--in' : 'cashier-dlg-badge--out'">
                        <i [class]="logType === 1 ? 'pi pi-arrow-down' : 'pi pi-arrow-up'"></i>
                        {{ dialogHeader }}
                    </span>
                </div>
            </ng-template>

            <div class="cashier-dlg-form">
                <div class="cashier-dlg-2col">
                    <div class="cashier-dlg-field">
                        <label for="cashDlgLogDate" class="block text-xs font-semibold text-color-secondary uppercase mb-1">Log date</label>
                        <p-datepicker
                            inputId="cashDlgLogDate"
                            [(ngModel)]="logDate"
                            [showIcon]="true"
                            appendTo="body"
                            [dateFormat]="vmsDatePickerFormat"
                            [disabled]="!isAdmin"
                            styleClass="w-full">
                        </p-datepicker>
                    </div>
                    <div class="cashier-dlg-field">
                        <label for="cashDlgName" class="block text-xs font-semibold text-color-secondary uppercase mb-1">Name <span class="text-red-500">*</span></label>
                        <p-autocomplete
                            inputId="cashDlgName"
                            [(ngModel)]="logName"
                            [suggestions]="filteredLogNameOptions"
                            appendTo="body"
                            dropdown
                            [forceSelection]="false"
                            [minLength]="0"
                            [delay]="0"
                            [showEmptyMessage]="true"
                            emptyMessage="No names found"
                            placeholder="Type or select"
                            autocomplete="off"
                            (completeMethod)="filterLogNames($event)"
                            (onSelect)="onLogNameSelect()"
                            (onBlur)="onLogNameBlur()"
                            styleClass="w-full">
                        </p-autocomplete>
                    </div>
                    <div class="cashier-dlg-field">
                        <label for="cashDlgSubName" class="block text-xs font-semibold text-color-secondary uppercase mb-1">Sub name</label>
                        <p-autocomplete
                            inputId="cashDlgSubName"
                            [(ngModel)]="logSubName"
                            [suggestions]="filteredLogSubNameOptions"
                            appendTo="body"
                            dropdown
                            [forceSelection]="false"
                            [minLength]="0"
                            [delay]="0"
                            [showEmptyMessage]="true"
                            emptyMessage="No sub names found"
                            placeholder="Optional (type or select)"
                            autocomplete="off"
                            (completeMethod)="filterLogSubNames($event)"
                            styleClass="w-full"
                            [disabled]="!logName">
                        </p-autocomplete>
                    </div>
                    <div class="cashier-dlg-field">
                        <label for="cashDlgAmt" class="block text-xs font-semibold text-color-secondary uppercase mb-1">Amount (₹) <span class="text-red-500">*</span></label>
                        <p-inputnumber
                            inputId="cashDlgAmt"
                            [(ngModel)]="amount"
                            mode="decimal"
                            [minFractionDigits]="2"
                            [maxFractionDigits]="2"
                            inputStyleClass="w-full"
                            styleClass="w-full"></p-inputnumber>
                    </div>
                    <div class="cashier-dlg-field">
                        <label for="cashDlgPay" class="block text-xs font-semibold text-color-secondary uppercase mb-1">Payment <span class="text-red-500">*</span></label>
                        <p-select
                            inputId="cashDlgPay"
                            [(ngModel)]="paymentMethodId"
                            [options]="paymentOptions"
                            optionLabel="name"
                            optionValue="keyID"
                            placeholder="Select method"
                            [filter]="true"
                            filterBy="name"
                            styleClass="w-full">
                        </p-select>
                    </div>
                </div>
            </div>

            <ng-template pTemplate="footer">
                <div class="flex justify-content-end align-items-center gap-2 w-full">
                    <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" size="small" (onClick)="close()"></p-button>
                    <p-button label="Save" icon="pi pi-check" size="small" (onClick)="save()"></p-button>
                </div>
            </ng-template>
        </p-dialog>
    `
})
export class CashInOutDialogComponent implements OnInit {
    vmsDatePickerFormat = VMS_DATEPICKER_FORMAT;
    visible = false;
    logType: CashierLogTypeCode = 1;

    isAdmin = false;
    logDate: Date = new Date();
    logName: string | null = null;
    logSubName: string | null = null;
    amount: number | null = null;
    paymentMethodId: number | null = null;

    logNameOptions: string[] = [];
    logSubNameOptions: string[] = [];
    filteredLogNameOptions: string[] = [];
    filteredLogSubNameOptions: string[] = [];
    paymentOptions: DropdownDataItem[] = [];
    private subNamesLoadedFor = '';

    private dropdownService = inject(DropdownService);
    private cashierService = inject(CashierService);
    private messages = inject(AppMessageService);
    private commonService = inject(CommonService);
    private host = inject(CashInOutDialogService);
    private destroyRef = inject(DestroyRef);

    get dialogHeader(): string {
        return this.logType === 1 ? 'Cash in' : 'Cash out';
    }

    ngOnInit(): void {
        this.isAdmin = this.commonService.userRoleId === 1;
        this.host.open$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((logType) => this.show(logType));
    }

    show(logType: CashierLogTypeCode): void {
        this.logType = logType;
        this.resetForm();
        forkJoin({
            payments: this.dropdownService.GetPaymentMethods(),
            logNames: this.dropdownService.GetCashierLogNames()
        }).subscribe({
            next: ({ payments, logNames }) => {
                this.paymentOptions = (payments || []).map(normalizeDd);
                this.logNameOptions = (logNames || []).map(normalizeDd).map((x) => x.name).filter(isNonEmptyString);
                this.filteredLogNameOptions = [...this.logNameOptions];
                this.visible = true;
            },
            error: () => this.messages.error('Could not load dropdown data')
        });
    }

    private resetForm(): void {
        this.logDate = new Date();
        this.logName = null;
        this.logSubName = null;
        this.amount = null;
        this.paymentMethodId = null;
        this.logSubNameOptions = [];
        this.filteredLogSubNameOptions = [];
        this.subNamesLoadedFor = '';
    }

    onLogNameSelect(): void {
        this.loadSubNamesFor(this.logName);
    }

    onLogNameBlur(): void {
        this.loadSubNamesFor(this.logName);
    }

    private loadSubNamesFor(name: string | null): void {
        const normalized = (name ?? '').trim();
        if (!normalized) {
            this.logSubName = null;
            this.logSubNameOptions = [];
            this.filteredLogSubNameOptions = [];
            this.subNamesLoadedFor = '';
            return;
        }
        if (this.subNamesLoadedFor.toLowerCase() === normalized.toLowerCase()) {
            return;
        }

        this.logSubName = null;
        this.logSubNameOptions = [];
        this.filteredLogSubNameOptions = [];
        this.dropdownService.GetCashierLogSubNames(normalized).subscribe({
            next: (rows) => {
                this.logSubNameOptions = (rows || []).map(normalizeDd).map((x) => x.name).filter(isNonEmptyString);
                this.filteredLogSubNameOptions = [...this.logSubNameOptions];
                this.subNamesLoadedFor = normalized;
            },
            error: () => this.messages.error('Could not load sub names')
        });
    }

    filterLogNames(event: AutoCompleteCompleteEvent): void {
        const q = (event.query ?? '').trim().toLowerCase();
        const source = !q ? this.logNameOptions : this.logNameOptions.filter((x) => x.toLowerCase().includes(q));
        // PrimeNG clears loading only when suggestions input receives a new array reference.
        this.filteredLogNameOptions = [...source];
    }

    filterLogSubNames(event: AutoCompleteCompleteEvent): void {
        const q = (event.query ?? '').trim().toLowerCase();
        const source = !q ? this.logSubNameOptions : this.logSubNameOptions.filter((x) => x.toLowerCase().includes(q));
        this.filteredLogSubNameOptions = [...source];
    }

    close(): void {
        this.visible = false;
    }

    onHide(): void {
        this.resetForm();
    }

    save(): void {
        let msg = '';
        if (this.isAdmin && !this.logDate) {
            msg += 'Log date, ';
        }
        const name = (this.logName ?? '').trim();
        const subName = (this.logSubName ?? '').trim();
        if (!name) {
            msg += 'Name, ';
        }
        if (this.paymentMethodId == null || this.paymentMethodId === undefined) {
            msg += 'Payment method, ';
        }
        if (this.amount == null || this.amount === undefined) {
            msg += 'Amount, ';
        }
        if (msg) {
            this.messages.error(`Required: ${msg.replace(/, $/, '')}`);
            return;
        }
        if (this.amount! <= 0) {
            this.messages.error('Amount must be greater than 0');
            return;
        }

        const body: ISaveCashierLog = {
            logDate: logDateToIso(this.logDate),
            logType: this.logType,
            name,
            subName,
            amount: Number(this.amount),
            paymentMethodId: Number(this.paymentMethodId)
        };

        this.cashierService.saveLog(body).subscribe({
            next: () => {
                this.messages.success('Saved successfully');
                this.host.notifySaved();
                this.close();
            },
            error: () => this.messages.error('Save failed')
        });
    }
}
